import { useEffect, useState } from "react";
import { Bell, BellOff, Smartphone, Send, AlertTriangle, CheckCircle2, Trash2, Loader2 } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { Button } from "../ui/button";
import {
  enablePush, disablePush, getCurrentSubscription, pushSupported,
  notificationStatus, sendTestPush,
} from "../../lib/push";
import CardCollapse from "./CardCollapse";

const fmt = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

export default function PushSettings() {
  const qc = useQueryClient();
  const [status, setStatus] = useState({ supported: false, permission: "default", hasSubscription: false });
  const [busy, setBusy] = useState(false);
  const { data: subs = [] } = useQuery({
    queryKey: ["admin", "push-subscriptions"],
    queryFn: () => api.get("/admin/push/subscriptions").then((r) => r.data),
    refetchInterval: 30000,
  });
  const refresh = async () => {
    setStatus({
      supported: pushSupported(),
      permission: notificationStatus(),
      hasSubscription: !!(await getCurrentSubscription()),
    });
    qc.invalidateQueries({ queryKey: ["admin", "push-subscriptions"] });
  };
  useEffect(() => { refresh(); }, []);

  const subscribe = async () => {
    setBusy(true);
    try {
      await enablePush();
      toast.success("Mobil bildirimler açıldı");
      await refresh();
    } catch (e) {
      toast.error(e.message || errMsg(e));
    } finally { setBusy(false); }
  };
  const unsubscribe = async () => {
    setBusy(true);
    try {
      await disablePush();
      toast.success("Bu cihazdan bildirim kapatıldı");
      await refresh();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const test = async () => {
    setBusy(true);
    try {
      await sendTestPush();
      toast.success("Test bildirimi gönderildi — telefonunuzu kontrol edin");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const removeDevice = async (endpoint) => {
    try {
      await api.delete(`/admin/push/subscriptions/${encodeURIComponent(endpoint.slice(0, 60))}`);
      toast.success("Cihaz silindi");
      refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const banner = () => {
    if (!status.supported)
      return { tone: "amber", text: "Bu tarayıcı Web Push desteklemiyor. Chrome, Edge, Firefox veya Safari 16.4+ önerilir." };
    if (status.permission === "denied")
      return { tone: "rose", text: "Bildirim izni engellendi. Tarayıcı ayarlarından açtıktan sonra tekrar deneyin." };
    if (status.hasSubscription)
      return { tone: "emerald", text: "Bu cihazda bildirimler aktif. Telegram'a ek olarak kilit ekranınıza da bildirim gelecek." };
    return { tone: "stone", text: "Henüz bu cihazda bildirim açılmadı." };
  };
  const b = banner();
  const toneCls = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    rose: "bg-rose-50 border-rose-200 text-rose-800",
    stone: "bg-stone-50 border-stone-200 text-stone-700",
  }[b.tone];
  const Icon = status.hasSubscription ? CheckCircle2 : (b.tone === "rose" ? AlertTriangle : Smartphone);

  return (
    <CardCollapse
      testid="push-settings-card"
      icon={<Bell size={16} />}
      title="Mobil Bildirimler (PWA)"
      description="Telegram'a ek olarak, admin panelinizi iOS/Android ana ekranına 'uygulama' gibi ekleyin ve push bildirimleri alın."
      defaultOpen={false}
    >
      <div className={`rounded-lg border p-3 text-sm flex items-start gap-2 ${toneCls}`} data-testid="push-status-banner">
        <Icon size={16} className="mt-0.5 flex-shrink-0" />
        <div className="flex-1">{b.text}</div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mt-4">
        {!status.hasSubscription ? (
          <Button onClick={subscribe} disabled={busy || !status.supported || status.permission === "denied"} data-testid="push-enable">
            {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Bell size={14} className="mr-1" />}
            Bu cihazda bildirimleri aç
          </Button>
        ) : (
          <Button variant="outline" onClick={unsubscribe} disabled={busy} data-testid="push-disable">
            <BellOff size={14} className="mr-1" /> Bu cihazda kapat
          </Button>
        )}
        <Button variant="outline" onClick={test} disabled={busy || subs.length === 0} data-testid="push-test">
          <Send size={14} className="mr-1" /> Tüm cihazlara test gönder
        </Button>
      </div>

      <div className="mt-5 bg-stone-50 border rounded-lg p-3 text-xs text-stone-600 space-y-1.5">
        <div className="font-semibold text-stone-700 text-sm mb-1">📱 iPhone/iPad kurulumu</div>
        <div>1. Siteyi <strong>Safari</strong> ile açın (Chrome değil)</div>
        <div>2. Alt menüdeki <strong>Paylaş</strong> butonuna → <strong>Ana Ekrana Ekle</strong></div>
        <div>3. Ana ekrandan uygulamayı açın, admin'e girin, bu sayfadan bildirimleri açın</div>
        <div className="font-semibold text-stone-700 text-sm pt-2 mt-2 border-t">🤖 Android kurulumu</div>
        <div>1. Chrome ile admin'e girin, tarayıcı menüsünden <strong>Ana ekrana ekle</strong></div>
        <div>2. Bu sayfadan "Bu cihazda bildirimleri aç" butonuna basın</div>
      </div>

      {subs.length > 0 && (
        <div className="mt-5">
          <div className="text-xs text-stone-500 font-semibold uppercase tracking-wider mb-2">Abone cihazlar ({subs.length})</div>
          <div className="space-y-1.5">
            {subs.map((sub) => (
              <div key={sub.endpoint} className="flex items-center justify-between gap-3 p-2.5 bg-white border rounded-lg text-sm" data-testid={`push-sub-${sub.endpoint.slice(0, 20)}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-[11px] text-stone-500 truncate">{(sub.label || sub.user_agent || "Cihaz").slice(0, 80)}</div>
                  <div className="text-[10px] text-stone-400 mt-0.5">{fmt(sub.created_at)}</div>
                </div>
                <button onClick={() => removeDevice(sub.endpoint)} className="text-stone-400 hover:text-red-600" data-testid={`push-rm-${sub.endpoint.slice(0, 20)}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </CardCollapse>
  );
}
